import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  checkIsMuxFailed,
  checkIsMuxLoading,
  getMuxAssetImageByPolicy,
  getMuxAssetStreamByPolicy,
} from '@distribute/shared/utils';

import {
  Avatar,
  Icon,
  SelectableInput,
  TabContent,
  Tabs,
  TimeAgo,
} from '../../../shared/ui';

import { IconMap } from '../../../shared/sprite';

import { DEFAULT_NAME } from '../../recordings/config';
import { authUserModel } from '../../../entities/auth-user';
import { callModel } from '../model';

import {
  CALL_DETAIL_TABS,
  CallDetailTab,
} from '../../../entities/call-recordings';

import { CallSummaryView } from './CallSummaryView';
import { CallTranscript } from './CallTranscript';
import { MEETING_PLATFORMS } from '../../../entities/call-recordings';

import {
  VideoCardStatus,
  VideoPlayer,
  VideoStatus,
} from '../../../entities/video';

export const CallDetails: React.FC = () => {
  const dispatch = useDispatch();

  const call = useSelector(callModel.selectors.selectCurrentCallWithError);
  const currentUser = useSelector(authUserModel.selectors.selectUserWithError);

  const [selectedTab, setSelectedTab] = useState<TabContent>(
    CALL_DETAIL_TABS[0]
  );

  const handleNameUpdate = (name: string) => {
    dispatch(
      callModel.actions.updateCallItemWithDebounce({
        id: call.id,
        data: { name },
      })
    );
  };

  const { muxAsset, userToTeam, isPrivate, createdAt, platform, attendees } =
    call;

  const owner = userToTeam.user;
  const isOwner = currentUser.id === owner.id;

  const currentPlatform =
    MEETING_PLATFORMS[platform] ?? MEETING_PLATFORMS.googleMeet;

  return (
    <div className="h-[calc(100vh-64px)] px-8 sm:px-0 overflow-y-auto">
      <div className="sm:px-5 flex flex-col md:max-w-full max-w-5xl mx-auto w-full min-h-full">
        <header className="flex flex-col gap-2 sticky top-0 bg-base-white sm:-mx-5 sm:px-5 sm:pt-5 sm:pb-4 pt-8 pb-6 z-10">
          <SelectableInput
            variant="titleXl"
            readonly={!isOwner}
            valueReadonly={call.name || DEFAULT_NAME}
            value={call.name}
            onChange={handleNameUpdate}
            placeholder="Untitled"
            classNames="w-full truncate"
          />
          <div className="flex justify-between items-center flex-nowrap gap-x-2">
            <div className="min-w-0 flex flex-nowrap gap-x-2 items-center text-gray-600 text-sm">
              <VideoCardStatus
                displayName={owner?.displayName}
                photoUrl={owner?.photoUrl ?? ''}
                PermissionStatus={
                  <VideoStatus
                    isTeamAccessible={!isPrivate}
                    teamMembersCount={1}
                  />
                }
              />
            </div>
            <div className="min-w-0 flex flex-nowrap items-center gap-2 text-gray-600">
              <Icon
                glyph={currentPlatform.icon}
                width={20}
                className="flex-shrink-0"
              />
              <span className="text-xs font-medium text-gray-500 flex-shrink-0 sm:hidden">
                {currentPlatform.name}
              </span>
              <Icon
                glyph={IconMap.Dot}
                width={5}
                className="flex-shrink-0 sm:hidden"
              />
              <TimeAgo
                datetime={createdAt}
                locale="my"
                className="flex-shrink-0 text-sm"
              />
            </div>
          </div>
        </header>
        <section className="flex gap-6 flex-1 pb-5">
          <div className="flex flex-col flex-shrink-0 md:max-w-full max-w-160 w-full">
            <VideoPlayer
              isLoading={checkIsMuxLoading(muxAsset)}
              isFailed={checkIsMuxFailed(muxAsset)}
              url={getMuxAssetStreamByPolicy(muxAsset)}
              posterUrl={getMuxAssetImageByPolicy(muxAsset)}
              aspectRatio={muxAsset.data.aspectRatio}
              hideBanner
              size="sm"
              className="sm:-mx-5"
            />
            <div className="flex flex-col flex-1">
              <div className="sm:pt-4 pt-6 pb-2 flex flex-col bg-base-white sticky z-10 sm:top-24 top-25">
                <Tabs
                  tabsOnly
                  tabClassName="!px-2"
                  className="!h-8 !mb-0"
                  tabs={CALL_DETAIL_TABS}
                  onSelectTab={setSelectedTab}
                  selectedTab={selectedTab}
                />
              </div>
              {selectedTab.name === CallDetailTab.SUMMARY && (
                <CallSummaryView call={call} />
              )}

              {selectedTab.name === CallDetailTab.TRANSCRIPT && (
                <CallTranscript call={call} />
              )}
            </div>
          </div>
          <aside className="min-w-0 w-full md:hidden relative">
            <div className="overflow-y-auto max-h-100 flex flex-col bg-gray-100 rounded-lg p-5 sticky top-31">
              <h3 className="flex items-center gap-1.5 text-lg font-semibold">
                Participants
                <span className="border border-gray-200 bg-gray-50 rounded-full px-2 py-0.5 text-gray-700 text-xs font-medium">
                  {attendees.length}
                </span>
              </h3>
              {attendees.length > 0 && (
                <ul>
                  {attendees.map((attendee) => (
                    <li
                      key={attendee.id}
                      className="flex items-center gap-2 mt-3"
                    >
                      <Avatar
                        className="border border-base-black/[0.08]"
                        src={undefined}
                        displayName={attendee.name}
                        size="sm"
                      />
                      <p className="text-sm text-gray-900 font-medium truncate">
                        {attendee.name}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};
